import { db, wipeAllTables, type SyncStateRow } from "./db.js";
import {
  listRecipes,
  createRecipe,
  getRecipe,
  patchRecipe,
  deleteRecipe,
  confirmPhoto,
  deletePhoto,
  postCdnCookies,
  clearCdnCookies,
} from "../features/recipes/api.js";
import { getTaxonomy } from "../features/taxonomy/api.js";
import {
  createCookSession,
  getOpenCookSessions,
  patchCookSession,
  waitCookSession,
  resumeCookSession,
  pauseCookSession,
  completeCookSession,
  abandonCookSession,
} from "../features/cook-sessions/api.js";
import {
  listShoppingLists,
  createShoppingList,
  patchShoppingList,
  deleteShoppingList,
  addShoppingListItem,
  patchShoppingListItem,
  deleteShoppingListItem,
  listShoppingCatalog,
} from "../features/shopping-lists/api.js";
import {
  listMealPlans,
  createMealPlan,
  patchMealPlan,
  deleteMealPlan,
} from "../features/meal-plans/api.js";
import { getShelfConfig, putShelfConfig } from "../features/shelves/api.js";
import { ApiError, getActiveHouseholdId } from "../shared/http.js";
import { toast } from "sonner";
import { ulid } from "ulid";
import type {
  ConfirmPhotoRequest,
  CreateRecipeRequest,
  PatchRecipeRequest,
  RecipeApiItem,
  TaxonomyApiItem,
  TaxonomyKind,
  CookSessionApiItem,
  CreateCookSessionRequest,
  PatchCookSessionRequest,
  WaitCookSessionRequest,
  CompleteCookSessionRequest,
  ShoppingListApiItem,
  ItemCatalogApiItem,
  CreateShoppingListRequest,
  PatchShoppingListRequest,
  AddShoppingListItemRequest,
  PatchShoppingListItemRequest,
  MealPlanApiItem,
  CreateMealPlanRequest,
  PatchMealPlanRequest,
  ShelfConfig,
  PutShelfConfigRequest,
} from "@recipevault/shared";
import { CDN_COOKIE_REFRESH_LEAD_SECONDS } from "@recipevault/shared";

const TAXONOMY_KINDS: TaxonomyKind[] = ["tag", "category", "cuisine", "diet"];

function nowIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

function minus60s(iso: string): string {
  const t = new Date(iso).getTime() - 60_000;
  return new Date(t).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Server is the authority on time (spec §3.2). Derive the delta watermark from the
 * highest server-minted `updatedAt` we actually observed — never the client clock.
 * Returns null when no rows were seen, so the caller keeps the prior watermark rather
 * than advancing past unseen writes.
 */
function maxUpdatedAt(items: ReadonlyArray<{ updatedAt: string }>): string | null {
  let max: string | null = null;
  for (const item of items) {
    if (max === null || item.updatedAt > max) max = item.updatedAt;
  }
  return max;
}

/**
 * Follow `nextCursor` to completion, accumulating every page. The backend defaults
 * to limit=100, so without this a library past 100 items would be silently truncated
 * (spec §5.1 cursor pagination). Returns ALL items across all pages.
 */
async function listAllRecipes(opts: { updatedSince?: string }): Promise<RecipeApiItem[]> {
  const all: RecipeApiItem[] = [];
  let cursor: string | undefined;
  do {
    const res = await listRecipes({ ...opts, ...(cursor ? { cursor } : {}) });
    all.push(...res.items);
    cursor = res.nextCursor ?? undefined;
  } while (cursor);
  return all;
}

async function listAllTaxonomy(
  kind: TaxonomyKind,
  opts: { updatedSince?: string },
): Promise<TaxonomyApiItem[]> {
  const all: TaxonomyApiItem[] = [];
  let cursor: string | undefined;
  do {
    const res = await getTaxonomy(kind, { ...opts, ...(cursor ? { cursor } : {}) });
    all.push(...res.items);
    cursor = res.nextCursor ?? undefined;
  } while (cursor);
  return all;
}

async function readSyncState(entity: SyncStateRow["entity"]): Promise<string | null> {
  const row = await db.sync_state.get(entity);
  return row?.lastSyncAt ?? null;
}

async function writeSyncState(entity: SyncStateRow["entity"], iso: string): Promise<void> {
  await db.sync_state.put({ entity, lastSyncAt: iso });
}

export class SyncService {
  // CDN-cookie refresh state — module-scope timer + in-flight de-dup promise.
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlightRefresh: Promise<void> | null = null;

  private scheduleNextRefresh(expiresAtIso: string): void {
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
    const expiresMs = new Date(expiresAtIso).getTime();
    const refreshAtMs = expiresMs - CDN_COOKIE_REFRESH_LEAD_SECONDS * 1000;
    const delayMs = Math.max(0, refreshAtMs - Date.now());
    this.refreshTimer = setTimeout(() => {
      void this.refreshCdnCookies();
    }, delayMs);
  }

  /**
   * Fetch fresh CloudFront signed cookies. Idempotent + coalesced —
   * concurrent callers share one network request.
   *
   * Triggered:
   *   - At bootstrap (eager)
   *   - 30 min before previous cookie expires (proactive, via setTimeout)
   *   - On photo <img> onerror (reactive, via RecipePhotoImg)
   *   - On household switch (post-me-active-household callback)
   */
  async refreshCdnCookies(): Promise<void> {
    if (this.inFlightRefresh) return this.inFlightRefresh;
    this.inFlightRefresh = (async () => {
      try {
        const res = await postCdnCookies();
        this.scheduleNextRefresh(res.expiresAt);
      } finally {
        this.inFlightRefresh = null;
      }
    })();
    return this.inFlightRefresh;
  }

  /** Clears the refresh timer. Call on logout to stop background refreshing. */
  stopCookieRefresh(): void {
    if (this.refreshTimer != null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Revoke CloudFront signed cookies server-side. Call on logout or household
   * switch to ensure the browser removes the HttpOnly cookies it cannot clear
   * itself. Best-effort — failures are logged but do not block the caller.
   */
  async clearPhotoCookies(): Promise<void> {
    try {
      await clearCdnCookies();
    } catch (err) {
      // Cookie clearing is best-effort — don't block logout.
      console.warn("clearCdnCookies failed", err);
    }
  }

  /**
   * If the active household changed since we last synced, the cache is another
   * household's data — wipe and force a fresh bootstrap. The sentinel row's
   * `lastSyncAt` field carries the last-synced householdId (not a timestamp),
   * reusing the existing row shape to avoid a Dexie schema bump.
   */
  private async guardHouseholdChange(current: string): Promise<"fresh" | "same"> {
    const row = await db.sync_state.get("__household");
    const changed = row !== undefined && row.lastSyncAt !== current;
    if (changed) {
      await wipeAllTables();
    }
    await db.sync_state.put({ entity: "__household", lastSyncAt: current });
    return changed ? "fresh" : "same";
  }

  /**
   * One-time full sync on first authenticated session (spec §6.2 "On login bootstrap
   * (one-time)"). Called only when sync_state is absent (gated in useSync); every
   * later app-open runs delta() instead.
   *
   * Bootstrap is AUTHORITATIVE: the server response is the complete live set, so we
   * clear Dexie then write the live rows inside one transaction. This reconciles
   * deletions that happened while the app was closed — a recipe the partner deleted
   * is simply absent from the authoritative set and gets dropped, rather than living
   * in the cache forever (its old tombstone is never re-fetched by future deltas).
   *
   * Paginates to completion (backend defaults limit=100) BEFORE writing sync_state,
   * so a library past 100 items isn't silently truncated.
   *
   * The watermark is the max server-minted `updatedAt` observed (server is the time
   * authority, spec §3.2) — falling back to the start-of-request client time only
   * when the library is empty (nothing to be stale against).
   */
  async bootstrap(): Promise<void> {
    const currentHousehold = getActiveHouseholdId();
    if (currentHousehold) await this.guardHouseholdChange(currentHousehold);

    const startedFallback = nowIso();
    const [recipes, taxonomyPages] = await Promise.all([
      listAllRecipes({}),
      Promise.all(TAXONOMY_KINDS.map((kind) => listAllTaxonomy(kind, {}))),
      this.refreshCdnCookies(),
    ]);
    const taxonomy = taxonomyPages.flat();

    const liveRecipes = recipes.filter((r) => !r.deletedAt);
    const liveTaxonomy = taxonomy.filter((t) => !t.deletedAt);

    const recipesWatermark = maxUpdatedAt(recipes) ?? startedFallback;
    const taxonomyWatermark = maxUpdatedAt(taxonomy) ?? startedFallback;

    await db.transaction("rw", db.recipes, db.taxonomy, db.sync_state, async () => {
      // Clear-then-bulkPut: authoritative reconciliation of deletions.
      await db.recipes.clear();
      await db.recipes.bulkPut(liveRecipes);
      await writeSyncState("recipes", recipesWatermark);

      await db.taxonomy.clear();
      await db.taxonomy.bulkPut(liveTaxonomy);
      await writeSyncState("taxonomy", taxonomyWatermark);
    });

    void this.syncCookSessions().catch(() => { /* non-blocking; next delta retries */ });
    void this.syncShoppingLists().catch(() => {});
    void this.syncItemCatalog().catch(() => {});
    void this.syncMealPlans().catch(() => {});
    void this.syncShelfConfig().catch(() => {});
  }

  /**
   * Background refresh: per-entity `?updatedSince=<lastSyncAt - 60s>`. The 60s
   * overlap absorbs GSI projection lag. Soft-deleted rows are applied as Dexie
   * deletes so partner deletions propagate.
   *
   * Paginates to completion (spec §5.1) BEFORE advancing the watermark — a truncated
   * page must never advance sync_state or overflow updates are lost permanently.
   *
   * The watermark advances to the max server-minted `updatedAt` actually seen this
   * pass (server is the time authority). When no rows came back, the prior watermark
   * is retained — we don't advance past writes we never observed.
   */
  async delta(): Promise<void> {
    const currentHousehold = getActiveHouseholdId();
    if (currentHousehold && (await this.guardHouseholdChange(currentHousehold)) === "fresh") {
      await this.bootstrap();
      return;
    }

    const recipesSince = (await readSyncState("recipes")) ?? "1970-01-01T00:00:00Z";
    const taxonomySince = (await readSyncState("taxonomy")) ?? "1970-01-01T00:00:00Z";

    const recipesSinceOverlap = minus60s(recipesSince);
    const taxonomySinceOverlap = minus60s(taxonomySince);

    const [recipes, taxonomyPages] = await Promise.all([
      listAllRecipes({ updatedSince: recipesSinceOverlap }),
      Promise.all(
        TAXONOMY_KINDS.map((kind) => listAllTaxonomy(kind, { updatedSince: taxonomySinceOverlap })),
      ),
    ]);
    const taxonomy = taxonomyPages.flat();

    // Advance only to what we actually saw; null → keep prior watermark.
    const recipesWatermark = maxUpdatedAt(recipes) ?? recipesSince;
    const taxonomyWatermark = maxUpdatedAt(taxonomy) ?? taxonomySince;

    await db.transaction("rw", db.recipes, db.taxonomy, db.sync_state, async () => {
      // Soft-deleted rows: remove from Dexie. Live rows: upsert.
      for (const r of recipes) {
        if (r.deletedAt) {
          await db.recipes.delete(r.id);
        } else {
          await db.recipes.put(r);
        }
      }
      await writeSyncState("recipes", recipesWatermark);

      for (const t of taxonomy) {
        if (t.deletedAt) {
          await db.taxonomy.delete(t.id);
        } else {
          await db.taxonomy.put(t);
        }
      }
      await writeSyncState("taxonomy", taxonomyWatermark);
    });

    void this.syncCookSessions().catch(() => { /* non-blocking; next delta retries */ });
    void this.syncShoppingLists().catch(() => {});
    void this.syncItemCatalog().catch(() => {});
    void this.syncMealPlans().catch(() => {});
    void this.syncShelfConfig().catch(() => {});
  }

  /**
   * Optimistic create. Caller already has the household-scoped ULID for the recipe
   * client-side? No — server mints `rec_<ulid>`. So we cannot pre-emptively write
   * Dexie with the final id. Instead:
   *   1. Try POST.
   *   2. On success, write Dexie with the server response.
   *   3. On failure, surface to caller (no optimistic state to roll back).
   * Idempotency-key prevents dupes on retry.
   */
  async createRecipeOptimistic(draft: CreateRecipeRequest): Promise<RecipeApiItem> {
    const idempotencyKey = ulid();
    const { recipe } = await createRecipe(draft, idempotencyKey);
    await db.recipes.put(recipe);
    return recipe;
  }

  /**
   * Optimistic patch. Write Dexie first, then POST.
   * On VERSION_CONFLICT, refetch the canonical row, overwrite Dexie, toast the user.
   * On any other error, refetch + overwrite Dexie (server is authoritative).
   */
  async patchRecipeOptimistic(id: string, body: PatchRecipeRequest): Promise<RecipeApiItem> {
    const before = await db.recipes.get(id);

    // Apply optimistic patch (best-effort — schema may not match exactly post-patch
    // but the next sync overrides).
    if (before) {
      const optimistic: RecipeApiItem = {
        ...before,
        ...(body.title !== undefined ? { title: body.title } : {}),
        ...(body.source !== undefined ? { source: body.source } : {}),
        ...(body.ingredients !== undefined ? { ingredients: body.ingredients } : {}),
        ...(body.steps !== undefined ? { steps: body.steps } : {}),
        ...(body.taxonomy !== undefined ? { taxonomy: body.taxonomy } : {}),
        // Plan-08 editable fields — without these the editor shows stale values
        // until the server response lands.
        ...(body.description !== undefined ? { description: body.description } : {}),
        ...(body.notes !== undefined ? { notes: body.notes } : {}),
        ...(body.servings !== undefined ? { servings: body.servings } : {}),
        ...(body.totalTimeMin !== undefined ? { totalTimeMin: body.totalTimeMin } : {}),
        ...(body.activeTimeMin !== undefined ? { activeTimeMin: body.activeTimeMin } : {}),
        ...(body.difficulty !== undefined ? { difficulty: body.difficulty } : {}),
        ...(body.rating !== undefined ? { rating: body.rating } : {}),
        // Intent flag (Plan 13) — absent means unchanged; reflect it locally so
        // the shelf/toggle UI updates before the server round-trip lands.
        ...(body.wantToTry !== undefined ? { wantToTry: body.wantToTry } : {}),
        updatedAt: nowIso(),
      };
      await db.recipes.put(optimistic);
    }

    try {
      const { recipe } = await patchRecipe(id, body);
      await db.recipes.put(recipe);
      return recipe;
    } catch (err) {
      // Roll back to server truth.
      try {
        const { recipe } = await getRecipe(id);
        await db.recipes.put(recipe);
      } catch {
        // If refetch also fails (e.g. 410 deleted), drop the local row.
        await db.recipes.delete(id);
      }
      if (err instanceof ApiError && err.code === "VERSION_CONFLICT") {
        toast.error("This recipe was updated. Please review and try again.");
      }
      throw err;
    }
  }

  /**
   * Optimistic delete. Remove from Dexie immediately, then DELETE. On failure,
   * refetch and restore.
   */
  async deleteRecipeOptimistic(id: string): Promise<void> {
    const before = await db.recipes.get(id);
    await db.recipes.delete(id);
    try {
      await deleteRecipe(id);
    } catch (err) {
      // Restore the optimistic delete on failure.
      if (before) await db.recipes.put(before);
      throw err;
    }
  }

  /**
   * Confirm a photo (new upload or cover swap). Posts to the API, then writes
   * the server response directly to Dexie so the live query in RecipeEditor
   * picks it up immediately without waiting for the next delta sync.
   */
  async confirmPhotoOptimistic(
    recipeId: string,
    body: ConfirmPhotoRequest,
  ): Promise<RecipeApiItem> {
    const idempotencyKey = ulid();
    const { recipe } = await confirmPhoto(recipeId, body, idempotencyKey);
    await db.recipes.put(recipe);
    return recipe;
  }

  /**
   * Delete a photo. Posts to the API, then writes the server response to Dexie
   * so the live query in RecipeEditor picks it up immediately.
   */
  async deletePhotoOptimistic(
    recipeId: string,
    photoKey: string,
    version: number,
  ): Promise<RecipeApiItem> {
    const { recipe } = await deletePhoto(recipeId, photoKey, version);
    await db.recipes.put(recipe);
    return recipe;
  }

  /** Authoritative reconcile of OPEN sessions (terminal ones drop out of the GSI1 set). */
  async syncCookSessions(): Promise<void> {
    const open = await getOpenCookSessions();
    const liveIds = new Set(open.map((s) => s.id));
    await db.transaction("rw", db.cookSessions, async () => {
      const cached = await db.cookSessions.toArray();
      for (const c of cached) if (!liveIds.has(c.id)) await db.cookSessions.delete(c.id);
      await db.cookSessions.bulkPut(open);
    });
  }

  async createCookSessionOptimistic(body: CreateCookSessionRequest): Promise<CookSessionApiItem> {
    const idempotencyKey = ulid();
    const session = await createCookSession(body, idempotencyKey);
    await db.cookSessions.put(session);
    return session;
  }

  async patchCookSessionOptimistic(id: string, body: PatchCookSessionRequest): Promise<CookSessionApiItem> {
    const before = await db.cookSessions.get(id);
    if (before) {
      const optimistic: CookSessionApiItem = {
        ...before,
        ...(body.currentStepId !== undefined ? { currentStepId: body.currentStepId } : {}),
        ...(body.stepStates !== undefined ? { stepStates: body.stepStates } : {}),
        ...(body.checkedIngredients !== undefined ? { checkedIngredients: body.checkedIngredients } : {}),
        ...(body.activeTimers !== undefined ? { activeTimers: body.activeTimers } : {}),
        ...(body.notes !== undefined ? { notes: body.notes } : {}),
      };
      await db.cookSessions.put(optimistic);
    }
    const session = await patchCookSession(id, body);
    await db.cookSessions.put(session);
    return session;
  }

  async waitCookSessionOptimistic(id: string, body: WaitCookSessionRequest): Promise<CookSessionApiItem> {
    const session = await waitCookSession(id, body);
    await db.cookSessions.put(session);
    return session;
  }

  async resumeCookSessionOptimistic(id: string): Promise<CookSessionApiItem> {
    const session = await resumeCookSession(id);
    await db.cookSessions.put(session);
    return session;
  }

  async pauseCookSessionOptimistic(id: string): Promise<CookSessionApiItem> {
    const session = await pauseCookSession(id);
    await db.cookSessions.put(session);
    return session;
  }

  async completeCookSessionOptimistic(id: string, body: CompleteCookSessionRequest): Promise<CookSessionApiItem> {
    const session = await completeCookSession(id, body);
    await db.cookSessions.delete(id); // terminal — drops out of the open set

    // Same-device immediacy for the "want to try" auto-clear: the stats-aggregator
    // worker is the authoritative source (it bumps the sync watermark so the
    // partner's device picks it up via delta sync), but that arrives async. This
    // is Dexie-only — no server call.
    const r = await db.recipes.get(session.recipeId);
    if (r?.wantToTry) await db.recipes.put({ ...r, wantToTry: false });

    return session;
  }

  async abandonCookSessionOptimistic(id: string): Promise<void> {
    const before = await db.cookSessions.get(id);
    await db.cookSessions.delete(id);
    try {
      await abandonCookSession(id);
    } catch (err) {
      if (before) await db.cookSessions.put(before);
      throw err;
    }
  }

  async syncShoppingLists(): Promise<void> {
    const since = (await readSyncState("shoppingLists")) ?? "1970-01-01T00:00:00Z";
    const all: ShoppingListApiItem[] = [];
    let cursor: string | undefined;
    do {
      const res = await listShoppingLists({ updatedSince: minus60s(since), ...(cursor ? { cursor } : {}) });
      all.push(...res.items);
      cursor = res.nextCursor ?? undefined;
    } while (cursor);
    const watermark = maxUpdatedAt(all) ?? since;
    await db.transaction("rw", db.shoppingLists, db.sync_state, async () => {
      for (const l of all) {
        if (l.deletedAt) {
          await db.shoppingLists.delete(l.id);
        } else {
          await db.shoppingLists.put(l);
        }
      }
      await writeSyncState("shoppingLists", watermark);
    });
  }

  async syncItemCatalog(): Promise<void> {
    const since = (await readSyncState("itemCatalog")) ?? "1970-01-01T00:00:00Z";
    const all: ItemCatalogApiItem[] = [];
    let cursor: string | undefined;
    do {
      const res = await listShoppingCatalog({ updatedSince: minus60s(since), ...(cursor ? { cursor } : {}) });
      all.push(...res.items);
      cursor = res.nextCursor ?? undefined;
    } while (cursor);
    const watermark = maxUpdatedAt(all) ?? since;
    await db.transaction("rw", db.itemCatalog, db.sync_state, async () => {
      // No deletedAt branch (unlike the other entity deltas): the catalog ApiItem
      // contract omits deletedAt (catalogRowToApiItem strips it) and there is no
      // catalog-delete endpoint, so bulkPut of the live set is authoritative.
      await db.itemCatalog.bulkPut(all);
      await writeSyncState("itemCatalog", watermark);
    });
  }

  async createShoppingListOptimistic(body: CreateShoppingListRequest): Promise<ShoppingListApiItem> {
    const idempotencyKey = ulid();
    const list = await createShoppingList(body, idempotencyKey);
    await db.shoppingLists.put(list);
    return list;
  }

  async patchShoppingListOptimistic(id: string, body: PatchShoppingListRequest): Promise<ShoppingListApiItem> {
    const before = await db.shoppingLists.get(id);
    if (before) await db.shoppingLists.put({ ...before, ...body, updatedAt: nowIso() });
    try {
      const l = await patchShoppingList(id, body);
      await db.shoppingLists.put(l);
      return l;
    } catch (err) {
      if (before) await db.shoppingLists.put(before);
      throw err;
    }
  }

  async deleteShoppingListOptimistic(id: string): Promise<void> {
    const before = await db.shoppingLists.get(id);
    await db.shoppingLists.delete(id);
    try {
      await deleteShoppingList(id);
    } catch (err) {
      if (before) await db.shoppingLists.put(before);
      throw err;
    }
  }

  async addItemOptimistic(id: string, body: AddShoppingListItemRequest): Promise<ShoppingListApiItem> {
    // One idempotency key per logical add, reused across a network retry. A
    // committed-but-lost response would otherwise leave the user seeing no item,
    // prompting a blind re-add that the server merges into a doubled quantity
    // (mergeItems sums qty). Reusing the key lets server reserve-before-execute
    // return the cached result instead of re-merging.
    const idempotencyKey = ulid();
    try {
      const l = await addShoppingListItem(id, body, idempotencyKey);
      await db.shoppingLists.put(l);
      return l;
    } catch (err) {
      // A structured ApiError means the server responded — no lost-response
      // ambiguity, so surface it rather than retrying. A network/timeout error
      // may mean the write committed but the response was lost; retry ONCE with
      // the SAME key so the server de-dupes instead of double-counting.
      if (err instanceof ApiError) throw err;
      const l = await addShoppingListItem(id, body, idempotencyKey);
      await db.shoppingLists.put(l);
      return l;
    }
  }

  async patchItemOptimistic(id: string, itemId: string, body: PatchShoppingListItemRequest): Promise<ShoppingListApiItem> {
    const before = await db.shoppingLists.get(id);
    if (before) {
      await db.shoppingLists.put({
        ...before,
        items: before.items.map((i) => (i.id === itemId ? { ...i, ...body } : i)),
        updatedAt: nowIso(),
      });
    }
    try {
      const l = await patchShoppingListItem(id, itemId, body);
      await db.shoppingLists.put(l);
      return l;
    } catch (err) {
      if (before) await db.shoppingLists.put(before);
      throw err;
    }
  }

  async deleteItemOptimistic(id: string, itemId: string): Promise<ShoppingListApiItem> {
    const l = await deleteShoppingListItem(id, itemId);
    await db.shoppingLists.put(l);
    return l;
  }

  /**
   * Meal plans are keyed on `weekStartDate` (a calendar date), not a ULID `id` — so
   * every Dexie read/write/delete below is by weekStartDate, unlike the `id`-keyed
   * entities above.
   */
  async syncMealPlans(): Promise<void> {
    const since = (await readSyncState("mealPlans")) ?? "1970-01-01T00:00:00Z";
    const all: MealPlanApiItem[] = [];
    let cursor: string | undefined;
    do {
      const res = await listMealPlans({ updatedSince: minus60s(since), ...(cursor ? { cursor } : {}) });
      all.push(...res.items);
      cursor = res.nextCursor ?? undefined;
    } while (cursor);
    const watermark = maxUpdatedAt(all) ?? since;
    await db.transaction("rw", db.mealPlans, db.sync_state, async () => {
      for (const p of all) {
        if (p.deletedAt) {
          await db.mealPlans.delete(p.weekStartDate);
        } else {
          await db.mealPlans.put(p);
        }
      }
      await writeSyncState("mealPlans", watermark);
    });
  }

  // No idempotencyKey — the week's natural key (weekStart) makes create idempotent
  // server-side, unlike shopping lists / recipes which need a client-generated key.
  async createMealPlanOptimistic(body: CreateMealPlanRequest): Promise<MealPlanApiItem> {
    const plan = await createMealPlan(body);
    await db.mealPlans.put(plan);
    return plan;
  }

  async patchMealPlanOptimistic(weekStart: string, body: PatchMealPlanRequest): Promise<MealPlanApiItem> {
    const before = await db.mealPlans.get(weekStart);
    if (before) {
      await db.mealPlans.put({
        ...before,
        ...(body.days !== undefined ? { days: body.days } : {}),
        ...(body.tasks !== undefined ? { tasks: body.tasks } : {}),
        ...(body.cookingFor !== undefined ? { cookingFor: body.cookingFor } : {}),
        updatedAt: nowIso(),
      });
    }
    try {
      const plan = await patchMealPlan(weekStart, body);
      await db.mealPlans.put(plan);
      return plan;
    } catch (err) {
      if (before) await db.mealPlans.put(before);
      throw err;
    }
  }

  async deleteMealPlanOptimistic(weekStart: string): Promise<void> {
    const before = await db.mealPlans.get(weekStart);
    await db.mealPlans.delete(weekStart);
    try {
      await deleteMealPlan(weekStart);
    } catch (err) {
      if (before) await db.mealPlans.put(before);
      throw err;
    }
  }

  /**
   * Household-shared shelf-config document (Plan 13). Unlike the entity
   * deltas above, this is a single small doc with no pagination or watermark
   * — GET always returns the complete, current config (the backend
   * synthesizes a default-shaped one when no row exists yet), so a plain
   * overwrite is authoritative every time.
   */
  async syncShelfConfig(): Promise<void> {
    const config = await getShelfConfig();
    await db.shelfConfig.put({ _singleton: "config", ...config });
  }

  /**
   * Optimistic PUT. Unlike patchMealPlanOptimistic (a partial PATCH merged
   * onto the existing row), shelf-config PUT replaces the whole document, so
   * `body` alone is enough to write a complete optimistic row — no `before`
   * needed to merge into. `updatedBy` is server-controlled; until the PUT
   * response lands we carry the previous value forward (or "" pre-sync) as a
   * placeholder that nothing reads yet.
   *
   * Rollback on failure: restore the prior row if one existed, otherwise
   * delete the optimistic row so the cache goes back to its pre-sync (absent)
   * state — same "was there a `before`?" branch as patchMealPlanOptimistic.
   */
  async putShelfConfigOptimistic(body: PutShelfConfigRequest): Promise<ShelfConfig> {
    const before = await db.shelfConfig.get("config");
    await db.shelfConfig.put({
      _singleton: "config",
      ...body,
      updatedAt: nowIso(),
      updatedBy: before?.updatedBy ?? "",
    });

    try {
      const config = await putShelfConfig(body);
      await db.shelfConfig.put({ _singleton: "config", ...config });
      return config;
    } catch (err) {
      if (before) {
        await db.shelfConfig.put(before);
      } else {
        await db.shelfConfig.delete("config");
      }
      throw err;
    }
  }
}

export const sync = new SyncService();
